/**
 * @file design-status.ts
 * @description Чистые правила панели «Дизайны» (phase19.16, решения
 * пользователя 2026-10-12): строка состояния «в работе / на сайте», порядок
 * карточек, текст «назначен страницам», имена страховочных копий и проверка
 * занятого имени. Без React — проверяются тестами отдельно от разметки.
 *
 * «В работе» — точное совпадение с черновиком (`matchesDraft`), «На сайте» —
 * с опубликованным видом (`matchesPublished`); источник не запоминается
 * (позже, после Р3).
 */

import type { SiteDesignSummary } from "@chaiberi/shared";

import type { SiteDesignItem, SiteDesignPageRef } from "../api-site-designs";
import { formatShopDate } from "../shop-time";

/** Строки состояния над списком (одна, если в работе и на сайте одно и то же). */
export type DesignsStatusLine = { kind: "same"; text: string } | { kind: "split"; draft: string; published: string };

/** «Как было» или «Как было» (и ещё 1) — при нескольких одинаковых дизайнах. */
function namesText(items: readonly SiteDesignItem[]): string {
  const first = `«${items[0]!.name}»`;
  return items.length > 1 ? `${first} (и ещё ${items.length - 1})` : first;
}

/** Строка состояния по списку дизайнов. */
export function designsStatus(items: readonly SiteDesignItem[]): DesignsStatusLine {
  const draft = sortDesigns(items).filter((d) => d.matchesDraft);
  const published = sortDesigns(items).filter((d) => d.matchesPublished);
  const sameSet =
    draft.length > 0 && draft.length === published.length && draft.every((d) => d.matchesPublished);
  if (sameSet) return { kind: "same", text: `Сейчас в работе и на сайте: ${namesText(draft)}` };
  return {
    kind: "split",
    draft: `Сейчас в работе: ${draft.length > 0 ? namesText(draft) : "свой вид, не сохранён в дизайн"}`,
    published: `на сайте: ${published.length > 0 ? namesText(published) : "вид без сохранённого дизайна"}`,
  };
}

/** Ранг карточки: в работе → на сайте → остальные. */
function rank(item: SiteDesignItem): number {
  if (item.matchesDraft) return 0;
  if (item.matchesPublished) return 1;
  return 2;
}

/** Порядок карточек: в работе, на сайте, остальные — новые изменения сверху. */
export function sortDesigns(items: readonly SiteDesignItem[]): SiteDesignItem[] {
  return [...items].sort((a, b) => rank(a) - rank(b) || b.updatedAt.localeCompare(a.updatedAt));
}

/** «Дизайн назначен страницам «О нас», «Доставка» — …» (меню, окно и ответ 409). */
export function assignedPagesText(pages: readonly SiteDesignPageRef[], count: number): string {
  const names = pages.map((p) => `«${p.title}»`).join(", ");
  const rest = count > pages.length ? ` и ещё ${count - pages.length}` : "";
  const list = names ? `страницам ${names}${rest}` : `страницам (${count})`;
  return `Дизайн назначен ${list} — их вид на сайте изменился бы сразу. Создайте новый дизайн или снимите этот со страниц.`;
}

/**
 * Дизайн без логотипов «совпадает» с черновиком, где логотипы есть: сервер
 * не сравнивает пустые поля бренда. Тогда «Обновить» нужно — добавить логотипы.
 */
export function canAddLogos(item: SiteDesignItem, draftHasLogos: boolean): boolean {
  return item.matchesDraft && !item.summary.withLogos && draftHasLogos;
}

/** Почему «Обновить из текущего вида» недоступно; `undefined` — доступно. */
export function updateBlockedText(item: SiteDesignItem, draftHasLogos = false): string | undefined {
  if (item.assignedPagesCount > 0) return assignedPagesText(item.assignedPages, item.assignedPagesCount);
  if (item.matchesDraft && !canAddLogos(item, draftHasLogos)) return "Дизайн уже совпадает с черновиком";
  return undefined;
}

/** Подпись пункта меню: «Добавить логотипы…», если отличаются только логотипы. */
export function updateActionLabel(item: SiteDesignItem, draftHasLogos: boolean): string {
  return canAddLogos(item, draftHasLogos) ? "Добавить логотипы из текущего вида" : "Обновить из текущего вида";
}

/** Сравнение имён без регистра и пробелов по краям (сервер не пустит дубль). */
function sameName(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase("ru-RU") === b.trim().toLocaleLowerCase("ru-RU");
}

/** Имя уже занято другим дизайном. */
export function isDesignNameTaken(items: readonly SiteDesignItem[], name: string, exceptId?: string): boolean {
  return items.some((d) => d.id !== exceptId && sameName(d.name, name));
}

/** Номер в конце имени: « (2)». */
const NUMBER_SUFFIX = /\s\(\d+\)$/;

/** Окончание копии вида с сайта. */
const PUBLISHED_SUFFIX = " (на сайте)";

/** Предел названия дизайна — как у сервера. */
export const DESIGN_NAME_MAX = 60;

/** Основа + окончание в пределах 60 знаков: длинная основа подрезается. */
function withSuffix(base: string, suffix: string): string {
  return `${base.slice(0, Math.max(0, DESIGN_NAME_MAX - suffix.length)).trimEnd()}${suffix}`;
}

/** Свободное имя: «Черновик 12.10», занято — «Черновик 12.10 (2)» и т. д. */
export function uniqueDesignName(items: readonly SiteDesignItem[], base: string): string {
  const first = base.slice(0, DESIGN_NAME_MAX);
  if (!isDesignNameTaken(items, first)) return first;
  // «Черновик 12.10 (2)» → следующий «(3)», а не «(2) (2)».
  const root = base.replace(NUMBER_SUFFIX, "");
  for (let n = 2; ; n += 1) {
    const candidate = withSuffix(root, ` (${n})`);
    if (!isDesignNameTaken(items, candidate)) return candidate;
  }
}

/** Имя страховочной копии черновика: «Черновик 12.10» (дата магазина). */
export function draftCopyBaseName(now: Date = new Date()): string {
  return `Черновик ${formatShopDate(now.toISOString()).slice(0, 5)}`;
}

/** Имя копии опубликованного вида перед обновлением: «Осень 2026 (на сайте)». */
export function publishedCopyBaseName(designName: string): string {
  // Имя уже с окончанием («Как было (на сайте)») — второй раз не добавляем.
  if (designName.endsWith(PUBLISHED_SUFFIX)) return designName.slice(0, DESIGN_NAME_MAX);
  return withSuffix(designName, PUBLISHED_SUFFIX);
}

/** «Lora · Inter» — шрифты заголовков и текста (один, если совпадают). */
export function designFontsText({ fonts }: SiteDesignSummary): string {
  return fonts.body !== fonts.heading ? `${fonts.heading} · ${fonts.body}` : fonts.heading;
}
